import {Tabs} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {RefObject} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetServiceOrderIntegrationsQuery,
  useGetServiceOrderJobItemWorkQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {LabourTypeEnum} from '@dms/shared-aftersales';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIntegrationsEnabled} from '../../hooks/useIntegrationsEnabled';
import {EditLabourItemAdditionalInformation} from './components/EditLabourItemAdditionalInformation';
import {EditLabourItemForm} from './components/EditLabourItemForm';
import {EditLabourItemMazdaInformation} from './components/EditLabourItemMazdaInformation';

const BASIC_INFORMATION = 'BASIC_INFORMATION';
const ADDITIONAL_INFORMATION = 'ADDITIONAL_INFORMATION';

interface EditLabourItemProps extends TestIdProps {
  itemId: string;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  onClose: VoidFunction;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

export function EditLabourItem(props: EditLabourItemProps) {
  const {data: serviceOrderIntegrations} = useGetServiceOrderIntegrationsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId)}
  );

  const {currentData: itemData} = useGetServiceOrderJobItemWorkQuery({
    serviceCaseId: props.serviceCaseId,
    serviceItemId: props.itemId,
    serviceJobId: props.serviceJobId,
    serviceOrderId: props.serviceOrderId,
  });

  const {isClaraIntegrationEnabled, isMazdaIntegrationEnabled} = useIntegrationsEnabled({
    serviceCaseId: props.serviceCaseId,
    orderIntegrations: serviceOrderIntegrations ?? {orderIntegration: []},
  });

  const isMazdaSubcontractItem =
    isMazdaIntegrationEnabled && itemData?.priceType === LabourTypeEnum.subcontract;

  return (
    <Tabs
      tabs={[
        {
          content: <EditLabourItemForm {...props} />,
          title: i18n.t('general.labels.basicInformation'),
          id: BASIC_INFORMATION,
        },
        {
          content: (
            <VStack spacing={1}>
              <Show when={isClaraIntegrationEnabled}>
                <EditLabourItemAdditionalInformation
                  orderId={props.serviceOrderId}
                  serviceCaseId={props.serviceCaseId}
                  serviceJobId={props.serviceJobId}
                  serviceItemId={props.itemId}
                  data-testid={suffixTestId('additional-information', props)}
                />
              </Show>
              <Show when={isMazdaSubcontractItem}>
                <EditLabourItemMazdaInformation
                  orderId={props.serviceOrderId}
                  serviceCaseId={props.serviceCaseId}
                  serviceJobId={props.serviceJobId}
                  serviceItemId={props.itemId}
                  isFooterPinned={!isClaraIntegrationEnabled}
                  onClose={props.onClose}
                  data-testid={suffixTestId('mazda-additional-information', props)}
                />
              </Show>
            </VStack>
          ),
          title: i18n.t('general.labels.additionalInformation'),
          id: ADDITIONAL_INFORMATION,
          isDisabled: !isClaraIntegrationEnabled && !isMazdaSubcontractItem,
        },
      ]}
      variant="condensed"
    />
  );
}
